import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CompanyService } from '@app/features/companies/company.service';
import { ValidationRequestTypeLabelPipe } from '@app/features/contracts/validation/validation-request-type-label-pipe';
import { DocumentTags } from '@app/features/documents/document.model';
import { processSettingsEditorRoles } from '@app/features/users/user.model';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { HasRoleDirective } from '@app/shared/auth/has-role.directive';
import { LucidePlus } from '@lucide/angular';
import { BehaviorSubject, combineLatest, filter, map, switchMap } from 'rxjs';
import { DesignationChips } from '../designation-chips/designation-chips';
import {
  NotificationLineDialog,
  NotificationLineDialogData,
  NotificationLineDialogResult,
} from '../notification-line-dialog/notification-line-dialog';
import { NotificationPreviewPanel } from '../notification-preview/notification-preview';
import {
  describeCadence,
  generalLineOf,
  NotificationEvent,
  notificationEventDescriptions,
  notificationEventLabels,
  notificationFamilies,
  NotificationSettingLine,
  particularLinesOf,
} from '../notification-setting.model';
import { NotificationSettingService } from '../notification-setting.service';

/**
 * Le réglage d'un événement : sa ligne générale, ses lignes particulières et l'aperçu de leurs
 * destinataires. Consultable par tous ; modifiable par les rôles habilités
 * (RG-notification-droit-de-regler).
 */
@Component({
  selector: 'app-notification-event-detail',
  imports: [
    RouterLink,
    MatButtonModule,
    HasRoleDirective,
    DesignationChips,
    NotificationPreviewPanel,
    ValidationRequestTypeLabelPipe,
    LucidePlus,
  ],
  templateUrl: './notification-event-detail.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./notification-event-detail.scss'],
})
export class NotificationEventDetail {
  private readonly notificationSettingService = inject(NotificationSettingService);
  private readonly dialog = inject(MatDialog);
  private readonly reload$ = new BehaviorSubject<void>(undefined);

  protected readonly editorRoles = processSettingsEditorRoles;
  protected readonly canEdit = inject(AUTHENTICATED_USER_SERVICE).hasAnyRole(
    processSettingsEditorRoles,
  );
  protected readonly documentTagLabels = DocumentTags.labels;
  protected readonly describeCadence = describeCadence;

  private readonly companies = toSignal(inject(CompanyService).allCompanies$, { initialValue: [] });

  protected readonly setting = toSignal(
    combineLatest([
      inject(ActivatedRoute).paramMap.pipe(
        map((params) => params.get('event') as NotificationEvent | null),
        filter((event): event is NotificationEvent => !!event),
      ),
      this.reload$,
    ]).pipe(switchMap(([event]) => this.notificationSettingService.get(event))),
  );

  protected readonly view = computed(() => {
    const setting = this.setting();
    if (!setting) {
      return null;
    }
    const general = generalLineOf(setting);
    const nobody = setting.lines.every((line) => line.designations.length === 0);
    const family =
      notificationFamilies.find(({ family }) => family === setting.family)?.label ?? '';
    return {
      setting,
      label: notificationEventLabels[setting.event],
      description: notificationEventDescriptions[setting.event],
      tag: setting.reminder ? `Relance · ${family}` : family,
      general,
      generalEmptyLabel: nobody
        ? "Personne n'est notifié"
        : 'Aucun destinataire sur la ligne générale',
      particulars: particularLinesOf(setting),
    };
  });

  protected companyName(companyId: string): string {
    return this.companies().find((company) => company.id === companyId)?.name ?? 'Société inconnue';
  }

  openLine(line: NotificationSettingLine | null): void {
    const setting = this.setting();
    if (!setting) {
      return;
    }
    this.dialog
      .open<NotificationLineDialog, NotificationLineDialogData, NotificationLineDialogResult>(
        NotificationLineDialog,
        {
          data: { setting, line },
          width: '1180px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          disableClose: true,
        },
      )
      .afterClosed()
      .pipe(filter((result) => !!result))
      .subscribe(() => this.reload$.next());
  }
}
